import clsx from "clsx";
import { useState, useEffect, SyntheticEvent } from "react";

import { Autocomplete, TextField, Paper, Chip, ListItem, Box } from "@mui/material";

// Icons
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ClearIcon from "@mui/icons-material/Clear";
import { CustomPopper } from "@/Layouts/components/CustomPopper";
import { maxHeight } from "@/Common/helpers/visuals";
import resolveCitScheduledAccountNumbers from "@/Settings/helpers/SystemSettings/cit/resolveCitScheduledAccountNumbers";
import { ICITScheduledAccountNumbers } from "@/Settings/types/Common/model";

type TInitialValue = { label: string; order: number; hasOptions: boolean };

type TCitSelectMultipleProps = {
	options?: ICITScheduledAccountNumbers[];
	initialValue?: TInitialValue[];
	placeholder?: string;
	className?: string;
	disabled?: boolean;
	loading?: boolean;
	error?: boolean;
	disableClearable?: boolean;
	items?: number;
	onSettled: (value: TInitialValue[]) => void;
};

const CitSelectMultiple = (props: TCitSelectMultipleProps) => {
	const [value, setValue] = useState<TInitialValue[]>([]);
	const [open, setOpen] = useState(false);

	const eventsEmitter = (_event: SyntheticEvent, newValue: unknown, reason: string) => {
		switch (reason) {
			case "selectOption":
				setValue([...value, newValue as TInitialValue]);
				props.onSettled([...value, newValue as TInitialValue]);
				break;
			case "removeOption":
				setValue(newValue as TInitialValue[]);
				props.onSettled(newValue as TInitialValue[]);
				break;
			case "clear":
				setValue([]);
				props.onSettled([]);
				break;
			case "settled":
				if (value.every((item) => item.label !== (newValue as TInitialValue).label)) {
					const updated = [...value, newValue];
					setValue(updated as TInitialValue[]);
					props.onSettled(updated as TInitialValue[]);
				}
				break;
			default:
				break;
		}
	};

	useEffect(() => {
		if (props?.initialValue?.length) {
			const arrSorted = [...(props.initialValue ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
			setValue(arrSorted);
		}
		return () => {
			setValue([]);
			setOpen(false);
		};
	}, [props.initialValue]);

	return (
		<Paper elevation={0} sx={{ width: "100%", backgroundColor: "transparent" }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "flex-start",
					columnGap: "0.3rem",
				}}
			>
				<Autocomplete
					disabled={props.disabled}
					multiple
					disableCloseOnSelect
					disablePortal
					open={open}
					onOpen={() => {
						setOpen(true);
					}}
					onClose={() => {
						setOpen(false);
					}}
					value={value}
					onChange={eventsEmitter}
					options={resolveCitScheduledAccountNumbers(props.options, value)}
					sx={{
						minWidth: "100%",
						"& .MuiInputBase-root": {
							padding: "2px 30px 1px 2px !important",
						},
						"& .MuiAutocomplete-tag": {
							margin: "-1px 0px 0px 1px",
						},
					}}
					renderInput={(params) => {
						return (
							<TextField
								{...params}
								component="li"
								type={"button"}
								className={clsx({
									[props.className ?? ""]: true,
									"advance-select-open": open,
									"advance-select-expand-area": props.disableClearable,
									"advance-select-text-button-error": props.error,
								})}
								placeholder={props.placeholder ?? "Select..."}
								sx={{ width: "250px" }}
							/>
						);
					}}
					getOptionLabel={(option) => option.label}
					forcePopupIcon={!!props.options?.length}
					isOptionEqualToValue={() => true}
					renderTags={(renderValue, getTagProps) => {
						return renderValue.map((option, index) => {
							const { key, ...restProps } = getTagProps({ index });
							const keyIndex = `account-tag-${key}-${index}`;
							return (
								<Chip
									key={keyIndex}
									{...restProps}
									deleteIcon={props.disabled ? <></> : <ClearIcon />}
									label={option.label.toUpperCase()}
									size="small"
									sx={{
										margin: "0px 5px 0px",
										borderRadius: "4px",
										width: "60px",
										justifyContent: "space-between",
										fontSize: "12px",
										fontFamily: "var(--font-family-v2)",
										color: "var(--text-color)",
									}}
								/>
							);
						});
					}}
					renderOption={(renderProps, option) => {
						const eventMessage = option.hasOptions || !value?.length ? "selectOption" : "settled";
						return (
							<ListItem
								{...renderProps}
								sx={{
									backgroundColor: "white !important",
									display: "flex !important",
									alignItems: "center !important",
									justifyContent: "space-between !important",
									"&:hover": {
										backgroundColor: "var(--gray-background) !important",
									},
								}}
								onClick={(event) => eventsEmitter(event, option, eventMessage)}
							>
								<span>{option.label.toUpperCase()}</span>
							</ListItem>
						);
					}}
					PopperComponent={CustomPopper}
					popupIcon={<ArrowForwardIosRoundedIcon sx={{ padding: "4px", transform: "rotate(90deg)" }} />}
					ListboxProps={{
						className: "advanced-select-dropdown",
						sx: { maxHeight: maxHeight(props.items) },
					}}
				/>
			</Box>
		</Paper>
	);
};

export default CitSelectMultiple;
